import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import { nanoid } from "nanoid";
import React, { useMemo, useRef, useState } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  PhoneInput,
  usePhoneInput,
  type PhoneInputRefType,
} from "react-international-phone";
import "react-international-phone/style.css";
import { InputWithLabels } from "../../DataInput/_components/InputWithLabels";
import { Icon, IconLoading } from "../../Icon";
import Link from "../../Link/Link";
import { Skeleton } from "../../Skeleton";
import { mergeProps } from "../../utils/component-props";
import "./TelephoneInput.lib.css";
import styles from "./TelephoneInput.module.scss";
import {
  ControlledTelephoneInputProps,
  TelephoneDisplayProps,
  TelephoneInputProps,
} from "./types";

const cx = classNames.bind(styles);

const TelephoneInput = ({
  id,
  name,
  value,
  onChange,
  defaultCountry,
  supportedCountries,
  pinnedCountries,
  placeholder,
  disableDialCodeAndPrefix = true,
  isDisabled = false,
  invalidMessage,
  "aria-invalid": ariaInvalid,
  error,
  isFluid = true,
  isInline = false,
  isBusy = false,
  isLoading = false,
  leftSlot,
  flairSlot,
  ...restProps
}: TelephoneInputProps) => {
  const ref = useRef<PhoneInputRefType>(null);
  const [isFocused, setIsFocused] = useState(false);
  const supportedCountriesList = supportedCountries
    ? defaultCountries.filter((country) => {
        const { iso2 } = parseCountry(country);

        return supportedCountries.includes(iso2);
      })
    : defaultCountries;

  const getFormatAsPlaceholder = (phoneInputRef: PhoneInputRefType | null) => {
    if (!phoneInputRef) {
      return;
    }

    if (
      !phoneInputRef.state.country.format ||
      typeof ref.current?.state.country.format !== "string"
    ) {
      return;
    }

    return (phoneInputRef.state.country.format as string).replaceAll(".", "0");
  };

  const hasError = !!error || !!invalidMessage || !!ariaInvalid;

  return (
    <div className={cx("container-root", { isFluid, isInline })}>
      {leftSlot && leftSlot({ className: cx("left-slot") })}
      <Skeleton
        isLoading={isLoading}
        isBusy={isBusy}
      >
        <PhoneInput
          inputProps={
            // react-international-phone bundles @types/react@17, which lacks
            // the `--radix-${string}` index signature added to CSSProperties in
            // newer @types/react. The double-cast bypasses the cross-version
            // structural mismatch — there is no real runtime incompatibility.
            {
              id,
              name,
              ...restProps,
            } as unknown as React.ComponentProps<
              typeof PhoneInput
            >["inputProps"]
          }
          ref={ref}
          defaultCountry={defaultCountry ?? "us"}
          name={name}
          value={value ?? ""}
          onChange={(phone, meta) => {
            const actualPhoneValue =
              meta.inputValue.trim() !== "" ? phone : meta.inputValue;

            onChange?.(actualPhoneValue, meta);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          countries={supportedCountriesList}
          preferredCountries={pinnedCountries ?? ["us", "ca"]}
          disabled={isDisabled}
          placeholder={
            placeholder ??
            getFormatAsPlaceholder(ref.current) ??
            "Enter a phone number"
          }
          className={cx("container", hasError && "error")}
          inputClassName={cx(
            "input",
            hasError && "error",
            isDisabled && "disabled",
            isInline && "inline"
          )}
          disableDialCodeAndPrefix={disableDialCodeAndPrefix}
          countrySelectorStyleProps={{
            buttonClassName: cx(
              "selector-button",
              isFocused && "focus",
              hasError && "error",
              isDisabled && "disabled"
            ),
            className: cx("selector-container", { isInline }),
          }}
        />
      </Skeleton>
      {(flairSlot || isBusy) && (
        <div className={cx("flair-slot")}>
          {isBusy && (
            <IconLoading
              speed="fast"
              size={10}
            />
          )}
          {flairSlot?.({})}
        </div>
      )}
    </div>
  );
};

export const ControlledTelephoneInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledTelephoneInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Telephone input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ leftSlot, flairSlot, invalidMessageId, labelId, inputId }) => (
        <TelephoneInput
          id={inputId}
          labelledById={labelId}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          isInline={isInline}
          isFluid={isFluid}
          isBusy={isBusy}
          isLoading={isLoading}
          leftSlot={leftSlot}
          flairSlot={flairSlot}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

export const TelephoneDisplay = ({
  value,
  className,
  fallbackDisplay,
}: TelephoneDisplayProps) => {
  const id = useRef(nanoid());
  const { country, phone } = usePhoneInput({
    value: value ?? "",
  });

  if (!value) {
    return fallbackDisplay ?? null;
  }

  const validPhoneNumber = typeof phone === "string" && phone.length > 2;

  return (
    <p className={cx("display", className)}>
      {validPhoneNumber ? (
        <FlagImage
          iso2={country.iso2}
          className={cx("flag")}
          title={country.name}
          aria-label={`Phone number in ${country.name}`}
          id={id.current}
        />
      ) : (
        <span
          className={cx("flag", "custom")}
          aria-label="Unrecognized phone number"
          id={id.current}
        >
          <Icon
            variant="question"
            size={16}
          />
        </span>
      )}
      {validPhoneNumber ? (
        <Link
          anchorTagProps={{
            href: `tel:${value}`,
            "aria-describedby": id.current,
          }}
          label={value}
        />
      ) : (
        <span aria-describedby={id.current}>{value}</span>
      )}
    </p>
  );
};

export default TelephoneInput;
